import { A, useParams } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type AdminRun as AdminRunView, useAdminRun } from '../lib/admin';
import { ApiError } from '../lib/api';
import { useMe } from '../lib/session';
import { UNSET } from '../lib/unset';
import { CardGridSkeleton } from './CardGridSkeleton';
import { EmptyState } from './EmptyState';
import { PageHeader } from './PageHeader';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { RunStatePill } from './RunStatePill';

// The cross-org run lookup's container (`/admin/runs/:run_id`) — the route
// header in `routes/AdminRun.tsx` says why it is top-level. Mounted ONLY inside
// <AdminGate/>: its read is a staff read the server audits, so it is not asked
// until the gate has admitted the session (plan 101 A16 — it used to fire on
// mount, beside the gate's own `/v1/me`).

export function AdminRunPanel(props: { readonly token: string | null }): JSX.Element {
  const token = props.token;
  const params = useParams<{ run_id: string }>();
  const run = useAdminRun(() => params.run_id ?? null, token);
  const me = useMe(token);

  // A 404 is an ANSWER — no task anywhere carries that run id — so it renders as
  // a neutral empty state, never the boundary's red error box (whose Retry would
  // 404 forever): the `RunStreamRefusal` rule, and `DeniedState`'s for a 403.
  const notFound = (): boolean =>
    run.isError && run.error instanceof ApiError && run.error.status === 404;

  /** True when the operator holds a membership on the run's owning org. */
  const isMember = (row: AdminRunView): boolean =>
    (me.data?.memberships ?? []).some((m) => m.github_login === row.account_login);

  return (
    <>
      <PageHeader title={t('admin.run_title')} desc={t('admin.run_subtitle')} />
      <Show when={!notFound()} fallback={<EmptyState message={t('admin.run_not_found')} />}>
        <QueryBoundary
          query={run}
          loading={<CardGridSkeleton listClass="grid-cards-3" count={1} />}
          empty={<EmptyState message={t('admin.run_empty')} />}
        >
          {(row) => (
            <Panel title={row.run_id}>
              <dl class="facts">
                <div>
                  <dt>{t('admin.run_owner')}</dt>
                  <dd>{row.account_login}</dd>
                </div>
                <div>
                  <dt>{t('admin.run_state')}</dt>
                  <dd>
                    <RunStatePill state={row.run.state} />
                  </dd>
                </div>
                <div>
                  <dt>{t('admin.run_last_event')}</dt>
                  <dd>{row.run.last_kind ?? UNSET}</dd>
                </div>
                <div>
                  <dt>{t('admin.run_task')}</dt>
                  <dd>{row.task_id ?? UNSET}</dd>
                </div>
              </dl>
              <Show
                when={isMember(row)}
                fallback={<p class="admin-run__muted">{t('admin.run_no_membership')}</p>}
              >
                <A href={`/org/${encodeURIComponent(row.account_login)}/fleet/runs/${row.run_id}`}>
                  {t('admin.run_open_tenant')}
                </A>
              </Show>
            </Panel>
          )}
        </QueryBoundary>
      </Show>
    </>
  );
}
